import * as React from 'react';

import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import { useParams, useRouter } from 'js/lib/useRouter';

import { LoadingSection, PageGridContainer } from '@coursera/cds-core';

import { NotFound } from 'bundles/common/components/withNotFound';
import GrowthDiscovery from 'bundles/epic/clients/GrowthDiscovery';
import ReviewsHeader from 'bundles/reviews/components/ReviewsHeader';
import ReviewsPage from 'bundles/reviews/components/ReviewsPage';
import ReviewsTable from 'bundles/reviews/components/ReviewsTable';
import ReviewsVariantCHeader from 'bundles/reviews/components/ReviewsVariantCHeader';
import TopReviewsSection from 'bundles/reviews/components/TopReviewsSection';
import { useReviewsPageData } from 'bundles/reviews/hooks/useReviewsPageData';
import type { ReviewRouter } from 'bundles/reviews/types/reviewTypes';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

const NUM_TOP_REVIEWS_TO_SHOW = 12;

const ReviewsPageDirector = () => {
  const router = useRouter();
  const params = useParams();
  const slug = params?.slug ?? '';
  const programSlug = params?.programSlug;
  const variant = GrowthDiscovery.get('reviewsPageRedesignXP');

  const {
    loading,
    error,
    notFound,
    courseInfo,
    description,
    ratings,
    topReviews = [],
    schemaReviews,
    variantCHeaderData,
  } = useReviewsPageData({
    includeVariantCHeaderData: variant === 'variantC',
  });

  if (loading) {
    return <LoadingSection />;
  }

  if (!slug || notFound || error || !courseInfo?.name) {
    if (inServerContext) {
      throw new NotFoundError();
    }
    return <NotFound />;
  }

  const courseId = courseInfo.id.split('~')[1];
  const coursePageHref = programSlug
    ? `/programs/${programSlug}/learn/${courseInfo.slug}`
    : `/learn/${courseInfo.slug}`;

  const pageProps = {
    slug,
    courseInfo,
    schemaReviews: schemaReviews ?? [],
    router: router as ReviewRouter,
  };

  if (variant === 'control') {
    return <ReviewsPage {...pageProps} />;
  }

  if (variant === 'variantB') {
    return (
      <PageGridContainer>
        <ReviewsHeader
          programSlug={programSlug}
          courseName={courseInfo.name}
          courseId={courseId ?? ''}
          coursePageHref={coursePageHref}
          ratings={ratings}
          description={description}
        />
        <TopReviewsSection topReviews={topReviews.slice(0, NUM_TOP_REVIEWS_TO_SHOW)} />
        <ReviewsTable courseId={courseInfo.id} />
      </PageGridContainer>
    );
  }

  if (variant === 'variantC') {
    return (
      <PageGridContainer>
        <ReviewsVariantCHeader
          {...variantCHeaderData}
          difficultyLevel={variantCHeaderData?.difficultyLevel as DifficultyLevel}
          programSlug={programSlug}
          courseId={courseId ?? ''}
          courseName={variantCHeaderData?.courseName ?? courseInfo.name}
        />
      </PageGridContainer>
    );
  }

  return <ReviewsPage {...pageProps} />;
};

export default ReviewsPageDirector;
